import FormActionButtons from '../components/FormActionButtons';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { Box, Button, Divider, Grid, IconButton, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import { GridToolbarContainer } from '@mui/x-data-grid';
import axios from 'axios';
import DOMPurify from 'isomorphic-dompurify';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import moment from 'moment';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore';
import LocalGroceryStoreOutlinedIcon from '@mui/icons-material/LocalGroceryStoreOutlined';
import EmailIcon from '@mui/icons-material/Email';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsIcon from '@mui/icons-material/Sms';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import ChatIcon from '@mui/icons-material/Chat';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import PolicyIcon from '@mui/icons-material/Policy';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';

import { DataAPIContext, DELETE, GET_LIST } from '../api';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { escape_html, linkify, shorten_attachment_links, whatsapp_markup_to_html, ensure_conversation_is_active_synced } from '../chat/utils/chat_utils';
import AccessDeniedView from '../components/AccessDeniedView';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import NavigateBackBar from '../components/NavigateBackBar';
import PageMeta from '../components/PageMeta';
import RecordDetails, { RecordDetailsContext } from '../components/RecordDetails';
import RecordList from '../components/RecordList';
import RevisionHistoryDialog from '../components/RevisionHistoryDialog';
import TabPanel, { a11yProps } from '../components/TabPanel';
import config from '../config';
import { ReactComponent as ActionOpenIcon } from '../icons/action_open.svg';
import { ReactComponent as ResendEmailIcon } from '../icons/send_email.svg';
import { ContextStore } from '../store/contextStore';
import { disableMuiButton, enableMuiButton } from '../utils';
import { useIsWidthUp } from '../utils';
import LocationHistoryMap from './LocationHistoryMap';
import UserChatHistoryToolbar from './UserChatHistoryToolbar';
import UserProfilePermissions from './UserProfilePermissions';
import { DisplayField, formatLatestAppVersionsDisplay, LocationStatusWidget, NotificationStatusWidget, WHATSAPP_STATUS } from './utils';
import { ConfirmationDialogText } from './utils';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import UserProfileTrips from './UserProfileTrips';
import UserContactList from './UserContactList';
import EmailDeliveryFailure, { deliveryFailureHtml } from './EmailDeliveryFailure';

// Attachment CDN basenames follow "att-<name>-<sha512-hash>.<ext>" — ~140
// characters of hash that drown the history log. Show "<name>.<ext>" instead.
const friendlyAttachmentFilename = (url) => {
    let basename = (url || '').split('/').pop().split('?')[0].split('#')[0];
    try {
        basename = decodeURIComponent(basename);
    } catch (e) { /* malformed escape — keep the raw basename */ }
    const m = basename.match(/^att-(.+?)-[0-9a-f]{40,}(\.[^.]+)?$/i);
    return m ? `${m[1]}${m[2] || ''}` : (basename || 'Attachment');
};

// .jfif is a plain JPEG under another extension (sent as image media over
// SMS/WhatsApp), so render it as a thumbnail like the rest.
const isImageMediaUrl = (url) => /\.(jpe?g|jfif|png|gif|webp)(\?.*)?$/i.test(url);

const useStyles = makeStyles((theme) => {
    return {
        root: {
            border: `1px solid ${grey[200]}`,
            borderRadius: '5px',
            backgroundColor: '#FCFCFE',
        },
        userProfile: {
            overflow: 'auto',
            height: 'calc(100vh - 16rem) !important',
            [theme.breakpoints.down('sm')]: {
                height: 'calc(100vh - 21rem) !important',
            },
        },
        field: {
            padding: theme.spacing(2),
            maxHeight: '80px',
            overflow: 'auto',
            [theme.breakpoints.down('sm')]: {
                maxHeight: '70px',
                marginBottom: '0.5rem',
            },
        },
        label: {
            color: '#747474',
            fontSize: '12px',
            lineHeight: '16px',
            fontWeight: '400',
            marginBottom: '0.25rem',
        },
        value: {
            color: '#202020',
            fontSize: 'small',
            lineHeight: '24px',
            maxWidth: '350px',
            overflow: 'hidden',
        },
        widgetValue: {
            fontSize: 'small',
            lineHeight: '24px',
        },
        previewImage: {
            borderRadius: '10px',
        },
        tripListContainer: {
            [theme.breakpoints.down('sm')]: {
                maxHeight: 'none',
                overflow: 'auto',
            },
        },
        userTripList: {
            paddingTop: '2px',
            paddingBottom: '2px',
        },
        userTripListElement: {
            paddingTop: '0px',
            paddingBottom: '0px',
        },
        userTripListElementText: {
            marginTop: '2px',
            marginBottom: '2px',

        },
        buttonPanel: {
            backgroundColor: '#FCFCFE',
            zIndex: '100 !important',
        },

        modalContent: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '50%',
            height: '50%',
            overflowY: 'scroll',
            overflowWrap: 'break-word',
            whiteSpace: 'normal',
            backgroundColor: theme.palette.background.paper,
            border: '1px solid #bbb',
            borderRadius: '5px',
            boxShadow: theme.shadows[5],
            padding: theme.spacing(2, 4, 3),
            //fontFamily: 'Roboto,Helvetica,Arial,sans-serif',
        },
        recordList: {
            '&.MuiDataGrid-root': {
                [theme.breakpoints.down('sm')]: {
                    height: '84%',
                },
            },
        },
    };
});

const UserProfile = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { hasPerm, isCuratedPlanetUser, roleMap, token } = React.useContext(ContextStore);
    // The resend endpoints check that the message's recipient is a user the caller may see, so send the token.
    const resendOpts = () => ({ headers: { Authorization: `Token ${token}` } });

    const history = useHistory();
    const classes = useStyles();
    const deleteRef = React.useRef(null);
    const editRef = React.useRef(null);
    const resendEmailRef = React.useRef(null);
    const [syncActive, setSyncActive] = React.useState(false);
    const user = props.data;
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const [currentTab, setCurrentTab] = React.useState(() => {
        const urlParams = new URLSearchParams(window.location.search);
        return parseInt(urlParams.get('tab'), 10) || 0; // default to tab 0 if parameter is missing or not a number
    });
    const [, setActiveTrips] = React.useState([]);
    const [, setUpcomingTrips] = React.useState([]);
    const [, setPastTrips] = React.useState([]);

    React.useEffect(() => {
        const now = moment.utc();
        const active = [];
        const upcoming = [];
        const past = [];

        user?.guest_trips?.forEach((trip) => {
            let trip_start = trip.travel_date;
            let trip_start_datetime = moment.utc(trip.start_ts);
            let trip_end = trip_start_datetime.clone().add(trip.length, 'seconds');

            if (now.isAfter(trip_end)) {
                past.push({ ...trip, end_date: trip_end, travel_date: trip_start });
            } else if (now.isBefore(trip_start_datetime)) {
                upcoming.push({ ...trip, end_date: trip_end, travel_date: trip_start });
            } else {
                active.push({ ...trip, end_date: trip_end, travel_date: trip_start });
            }
        });

        setActiveTrips(active);
        setUpcomingTrips(upcoming);
        setPastTrips(past);
    }, [user?.guest_trips]);

    const chatListResource = 'user_message_log';
    const [chatTextExpandedRowID, setChatTextExpandedRowID] = React.useState(-1);
    const [chatListQuery, setChatListQuery] = React.useState(null);
    const [listIsLoading, setListIsLoading] = React.useState(false);
    const isSmallScreen = !useIsWidthUp('sm');
    const [chatAPIFilters, setChatAPIFilters] = React.useState([]);
    const [chatSelectedTourFilter, setChatSelectedTourFilter] = React.useState(null);

    const [chatListSortOrder, setChatListSortOrder] = React.useState('desc');
    const [isExporting] = React.useState(false);
    let emailResendRef = React.useRef(user.email);
    let phoneResendRef = React.useRef(user.phone);

    let printWasClickedRef = React.useRef(false);

    const componentToPrintRef = React.useRef();

    const handlePrint = useReactToPrint({
        content: () => componentToPrintRef.current,
    });
    const printRow = () => {
        console.log('printRow: Printing row');
        handlePrint();
        printWasClickedRef.current = false;
    };

    const handleEdit = (ev) => {
        console.log('editing', user.id);
        history.push({
            pathname: `/user/${user.id}/edit`,
            search: history.location.search || '',
        });
    };

    const doDelete = () => {
        setSyncActive(true);
        disableMuiButton(deleteRef.current);
        (async () => {
            // The push unmounts this page, so state must not be touched after it.
            let navigated = false;
            try {
                const res = await dataAPI(DELETE, props.resource, { id: user.id });
                if (res.statusCode === 200 || res.statusCode === 204) {
                    enqueueSnackbar('User deleted', { variant: 'default' });
                    history.push(`/user`, { deleted: user.id });
                    navigated = true;
                } else if (res.statusCode === 406) {
                    if (res.data && res.data.message) {
                        enqueueSnackbar(res.data.message, { variant: 'error', autoHideDuration: 5000 });
                    } else {
                        enqueueSnackbar('Error deleting user', { variant: 'error' });
                    }
                } else if (res.data?.message) {
                    enqueueSnackbar(res.data.message, { variant: 'error', autoHideDuration: 5000 });
                } else {
                    enqueueSnackbar('Error deleting user', { variant: 'error' });
                }
            } catch (e) {
                enqueueSnackbar('Error deleting user', { variant: 'error' });
            } finally {
                if (!navigated) {
                    setSyncActive(false);
                    if (deleteRef && deleteRef.current) {
                        enableMuiButton(deleteRef.current);
                    }
                }
            }
        })();
    };

    const getUserPlatform = (user) => formatLatestAppVersionsDisplay(user.latest_app_versions) || 'App Not Downloaded';

    const handleDelete = (ev) => {
        setConfirmationDialogAction('delete_user');
    };

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        console.log('confirm', confirmationDialogAction);
        switch (confirmationDialogAction) {
            case 'delete_user':
                doDelete();
                break;
            case 'resend_email':
                resendEmail();
                break;
            case 'resend_sms':
                resendSms();
                break;
            case 'resend_whatsapp':
                resendWhatsApp();
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    const handleTabChange = (ev, newTab) => {
        setCurrentTab(newTab);
        if (newTab === 4 && props.lastURLParams.size > 1) {
            history.push({
                pathname: history.location.pathname,
                search: props.lastURLParams.toString()
            });
            return;
        }
        history.push({
            pathname: history.location.pathname,
            search: `?tab=${newTab}`
        });
    };

    const tabPanelProps = (index, h) => {
        return {
            className: classes.tabPanel,
            value: currentTab,
            index: index,
            //dir: theme.direction,
            style: {
                height: isSmallScreen && '73vh',
            },
        };
    };

    const expandChatText = (e, idx) => {
        console.log('expandChatText', idx, e.target.tagName, componentToPrintRef.current);
        if (!(e.target.tagName === 'A') && !window.getSelection()?.toString()) {
            if (chatTextExpandedRowID === idx) {
                setChatTextExpandedRowID(-1);
            } else {
                setChatTextExpandedRowID(idx);
            }
        }
    };

    const expandChatTextAndPrint = (e, idx) => {
        console.log('expandChatTextAndPrint', idx, e.target.tagName, componentToPrintRef.current);
        if (!(e.target.tagName === 'A')) {
            printWasClickedRef.current = true;

            if (idx !== chatTextExpandedRowID) {
                // if new row is open/expanded, set the ref to null and let it be sent/printed in the new render
                // (per the chatTextExpandedRowID state update)
                componentToPrintRef.current = null;
            } else {
                // if row is already open/expanded
                printRow();
            }
            setChatTextExpandedRowID(idx);
        }
    };

    const onChatListQueryChange = (query) => {
        setChatListQuery(query);
    };

    const resendEmail = async () => {
        const params = { email_id: resendEmailRef.current, recipient_email_addr: emailResendRef.current ? emailResendRef.current : user.email };
        await axios
            .post(`${config.apiUrl}/resend_email/`, params, resendOpts())
            .then((response) => {
                console.debug('UserProfile: Email sent successfully!');
                enqueueSnackbar('Email sent', { variant: 'default' });
            })
            .catch((error) => {
                if (error.response) {
                    console.error('UserProfile: Error resending email!', error.response);
                    const reason = error.response.status === 409 ? error.response.data?.message : null;
                    enqueueSnackbar(reason ? `Email not sent. ${reason}` : 'Error sending email', { variant: 'error' });
                }
            });
    };

    function validateEmail(email) {
        var re = /\S+@\S+\.\S+/;
        return re.test(email);
    }

    function validateEmailMsg(email) {
        if (!validateEmail(email)) {
            return 'Please enter a valid email';
        } else {
            return null;
        }
    }

    const handleResendEmail = (ev) => {
        setConfirmationDialogAction('resend_email');
    };

    const resendSms = async () => {
        const params = { message_sid: resendEmailRef.current, recipient_phone: phoneResendRef.current ? phoneResendRef.current : user.phone };
        await axios
            .post(`${config.apiUrl}/resend_sms/`, params, resendOpts())
            .then((response) => {
                console.debug('UserProfile: SMS sent successfully!');
                enqueueSnackbar('SMS sent', { variant: 'default' });
            })
            .catch((error) => {
                if (error.response) {
                    console.error('UserProfile: Error resending SMS!', error.response);
                    enqueueSnackbar('Error sending SMS', { variant: 'error' });
                }
            });
    };

    const handleResendSms = (ev) => {
        setConfirmationDialogAction('resend_sms');
    };

    const resendWhatsApp = async () => {
        const params = { message_sid: resendEmailRef.current, recipient_phone: phoneResendRef.current ? phoneResendRef.current : user.phone };
        await axios
            .post(`${config.apiUrl}/resend_whatsapp/`, params, resendOpts())
            .then((response) => {
                console.debug('UserProfile: WhatsApp message sent successfully!');
                enqueueSnackbar('WhatsApp message sent', { variant: 'default' });
            })
            .catch((error) => {
                if (error.response) {
                    console.error('UserProfile: Error resending WhatsApp message!', error.response);
                    enqueueSnackbar('Error sending WhatsApp message', { variant: 'error' });
                }
            });
    };

    const handleResendWhatsApp = (ev) => {
        setConfirmationDialogAction('resend_whatsapp');
    };

    function validatePhone(phone) {
        var re = /^\+?[1-9]\d{6,14}$/;
        return re.test(phone?.replace(/[\s\-()]/g, ''));
    }

    function validatePhoneMsg(phone) {
        if (!validatePhone(phone)) {
            return 'Please enter a valid phone number';
        } else {
            return null;
        }
    }

    if (!hasPerm('user', 'read')) {
        <AccessDeniedView />;
    }

    React.useEffect(() => { }, [isExporting])

    const buildRowHtml = (row) => {
        const d1 = new Date(row.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const d2 = new Date(row.timestamp).toLocaleString('en-US', { hour: 'numeric', minute: 'numeric' });

        const role_map = { admin: 'Admin', guide: 'Tour Guide', guest: 'Guest' };
        let role_text = role_map[row.sender_conversation_role];
        if (row.sender_conversation_role === 'guest' && row.sender && row.sender.is_guide) role_text = 'Tour Guide';
        else if (row.sender_conversation_role === 'guest' && row.sender && row.sender.is_admin) role_text = 'Admin';

        const role_bg_color_map = { Admin: '#B7EBE3', 'Tour Guide': '#E4E4F9', Guest: '#F8D7DA' };

        // Email bodies are intentional HTML (see the is_html_email handling below). Every other
        // source is plain text, where a raw "<" would be parsed as a tag by linkify-html and the
        // rest of the message dropped by DOMPurify, so escape those before inserting <br>.
        const rawChatText = row.text_plus_media_url || '';
        let chatText = (row.source === 'email' ? rawChatText : escape_html(rawChatText))
            .split('\n').join('<br>').split('<p>\r\n').join('<p>').split('\r\n</p>').join('</p>');
        if (row.source === 'whatsapp') {
            // Convert WhatsApp markup to HTML for intuitive viewing, skipping URLs so links
            // containing ~/*/_ (e.g. Apple Maps short links) aren't mangled. See chat_utils.
            chatText = whatsapp_markup_to_html(chatText);
        }

        // CP-8116: collapse raw attachment/media URLs into short labeled links. Email
        // bodies are intentional HTML — never rewrite those (see chat_utils).
        if (row.source !== 'email') {
            chatText = shorten_attachment_links(chatText);
        }

        let sanitizedText = DOMPurify.sanitize(linkify(chatText), { ADD_ATTR: ['target'] });

        if (row.source === 'email') {
            let regex = /<[^>]*>/g;
            let emailText = row.text_plus_media_url?.replace(regex, (match) => match.replace(/[\n\r]/g, ''));
            const linebreaksAfterSubjectLine = '\n\n';
            const i1 = emailText?.indexOf(linebreaksAfterSubjectLine);
            let is_html_email = false;
            if (i1 > 1) {
                let bodyText = emailText.substring(i1 + linebreaksAfterSubjectLine.length);
                if (bodyText.trim().length >= 2 && bodyText.trim()[0] === '<' && bodyText.trim()[bodyText.trim().length - 1] === '>') {
                    is_html_email = true;
                }
            }
            if (!is_html_email) {
                sanitizedText = emailText?.split('<p>\r\n').join('<p>').split('\r\n</p>').join('</p>').split('\n').join('<br>') || '';
            } else {
                sanitizedText = (emailText || '')
                    .replace(/font-size\s*:\s*0\s*(px|pt)?\s*(?=[;"])/gi, '')
                    .replace(/line-height\s*:\s*0\s*(px|pt)?\s*(?=[;"])/gi, '');
            }
            sanitizedText = sanitizedText?.replace('<pre>', '<pre style="white-space: pre-wrap;">') || '';
        }

        let sent_via = row.source;
        if (sent_via === 'chat') sent_via = 'In-App';
        else if (sent_via === 'email') sent_via = 'Email';
        else if (sent_via === 'sms') sent_via = 'SMS';
        else if (sent_via === 'whatsapp') sent_via = 'WhatsApp';
        else if (sent_via === 'airbnb') sent_via = 'Airbnb';

        let conversation_display_name = '';
        const channelTopicID = row.associated_twilio_conversation_unique_name?.split('::')[0];
        const channelSubjectID = row.associated_twilio_conversation_unique_name?.split('::')[2];
        if (row.recipient) conversation_display_name = row.recipient.display_name;
        else if (channelSubjectID === 'announcements') conversation_display_name = 'Announcements';
        else if (channelSubjectID === 'guides_and_staff') conversation_display_name = 'INTERNAL: Guides/Staff';
        else if (channelSubjectID === 'group_chat') conversation_display_name = 'Group Chat';
        else if (channelTopicID === 'main_office') conversation_display_name = 'Main Office';
        else if (channelTopicID === 'app_support') conversation_display_name = 'App Support';
        else if (user.id === parseInt(channelSubjectID)) conversation_display_name = 'Tour Guides/Support';
        else conversation_display_name = 'Chat';

        const senderName = row.sender?.display_name || row.automated_message_sender_name || row.sender_name || '';
        const roleBadge = role_text ? `<span style="font-size:14px;background-color:${role_bg_color_map[role_text] || '#EEE'};padding:2px 8px;border-radius:2px;">${role_text}</span>` : '';
        const readStatus = row.was_read ? '&#10003;&#10003; Read' : (row.source === 'sms' ? '&#10003;&#10003; Delivered' : '&#10003;&#10003; Unread');
        const readColor = row.was_read ? '#007AFF' : '#888';
        const deliveryStatusHtml = row.delivery_failure
            ? deliveryFailureHtml(row.delivery_failure)
            : `<span style="color:${readColor};font-size:14px;">${readStatus}</span>`;
        const recipientInfo = row.recipient_email_address ? ` to ${row.recipient_email_address}` : (row.recipient_phone_number ? ` to ${row.recipient_phone_number}` : '');
        const eventLabel = row.event_code_summary_label ? `<span style="color:#F2994A;font-weight:500;">${row.event_code_summary_label}</span>` : '';
        const channelDate = row.associated_twilio_conversation_channel_name_date_inside ? `<span style="background-color:#EAF4FF;border-radius:2px;margin-left:7px;">${row.associated_twilio_conversation_channel_name_date_inside}</span>` : '';
        const bookingRefs = row.subject_gtus_booking_refs?.length ? `BR: ${row.subject_gtus_booking_refs[0]}${row.subject_gtus_booking_refs.length > 1 ? ' (+' + (row.subject_gtus_booking_refs.length - 1) + ')' : ''}` : '';

        let mediaHtml = '';
        if (Array.isArray(row.media_urls) && row.media_urls.length > 0) {
            const mediaItems = row.media_urls.map((url) => {
                if (isImageMediaUrl(url)) return `<img src="${url}" style="max-width:120px;max-height:120px;border-radius:6px;border:1px solid #ddd;object-fit:cover;display:block;" />`;
                const filename = friendlyAttachmentFilename(url);
                return `<span style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;background-color:#EEE;border-radius:6px;font-size:12px;color:#333;">&#128206; ${filename}</span>`;
            }).join('');
            mediaHtml = `<div style="margin-top:6px;display:flex;flex-wrap:wrap;gap:8px;">${mediaItems}</div>`;
        }

        return `
            <div style="font-size:16px;margin-top:8px;margin-bottom:8px;padding:8px 0;border-bottom:1px solid #E8E8E8;page-break-inside:avoid;">
                <div>
                    <span style="font-weight:500;">${senderName}</span> ${roleBadge}
                    <span> to </span>
                    <span style="font-weight:500;">${conversation_display_name}</span>
                    <span> sent </span>
                    <span style="font-weight:500;">${sent_via}</span>
                    ${eventLabel || channelDate || bookingRefs ? `<span style="font-size:14px;float:right;padding:2px 8px;">${eventLabel}${channelDate} ${bookingRefs}</span>` : ''}
                </div>
                <div>
                    ${deliveryStatusHtml}
                    <span style="color:#747475;font-size:14px;"> ${d1} at ${d2}${recipientInfo}</span>
                </div>
                <div style="font-size:12px;padding:8px 12px;background-color:#F5F5F5;border-radius:8px;margin-top:4px;overflow:visible;word-wrap:break-word;">
                    ${sanitizedText}
                </div>
                ${mediaHtml}
            </div>`;
    };

    const ExportPrintButton = () => {
        const [exporting, setExporting] = React.useState(false);

        const handleExportPDF = async () => {
            setExporting(true);
            try {
                const res = await dataAPI(GET_LIST, chatListResource + '/' + user.id.toString(), {
                    pagination: { page: 1, perPage: 9999 },
                    sort: { field: 'timestamp', order: chatListSortOrder === 'desc' ? 'DESC' : 'ASC' },
                    filter: {
                        query: chatListQuery,
                        filters: JSON.stringify(chatAPIFilters || []),
                    },
                });

                const rows = res.data || [];
                if (rows.length === 0) {
                    enqueueSnackbar('No messages to export', { variant: 'info' });
                    setExporting(false);
                    return;
                }

                const container = document.createElement('div');
                container.style.cssText = 'position:absolute;left:-9999px;top:0;width:10in;padding:0.25in;font-family:Roboto,Arial,sans-serif;background:#fff;';
                container.innerHTML = `
                    <h2 style="margin:0 0 4px 0;font-size:18px;">${user.display_name} — Message History</h2>
                    <div style="color:#747475;font-size:13px;margin-bottom:12px;">${rows.length} message${rows.length !== 1 ? 's' : ''} exported on ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                    ${rows.map(row => buildRowHtml(row)).join('')}
                `;
                document.body.appendChild(container);

                const pdfScale = 2;
                const fullCanvas = await html2canvas(container, { scale: pdfScale, useCORS: true, letterRendering: true, backgroundColor: '#ffffff' });

                const pdf = new jsPDF({ orientation: 'landscape', unit: 'in', format: 'letter' });
                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();
                const margin = 0.3;
                const usableWidth = pageWidth - margin * 2;
                const usableHeight = pageHeight - margin * 2;

                const pxPerInch = fullCanvas.width / (10 + 0.5);
                const sliceHeightPx = Math.floor(usableHeight * pxPerInch);
                const totalPages = Math.ceil(fullCanvas.height / sliceHeightPx);

                for (let i = 0; i < totalPages; i++) {
                    if (i > 0) pdf.addPage();
                    const srcY = i * sliceHeightPx;
                    const srcH = Math.min(sliceHeightPx, fullCanvas.height - srcY);

                    const pageCanvas = document.createElement('canvas');
                    pageCanvas.width = fullCanvas.width;
                    pageCanvas.height = srcH;
                    const ctx = pageCanvas.getContext('2d');
                    ctx.drawImage(fullCanvas, 0, srcY, fullCanvas.width, srcH, 0, 0, fullCanvas.width, srcH);

                    const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.95);
                    const imgH = (srcH / fullCanvas.width) * usableWidth;
                    pdf.addImage(pageImgData, 'JPEG', margin, margin, usableWidth, imgH);
                }

                pdf.save(`${user.display_name.replace(/[^a-zA-Z0-9]/g, '_')}_chat_history.pdf`);
                document.body.removeChild(container);
            } catch (err) {
                console.error('PDF export failed:', err);
                enqueueSnackbar('Error generating PDF', { variant: 'error' });
            }
            setExporting(false);
        };

        return (
            <Button
                title={"Export all filtered messages as PDF"}
                style={{
                    visibility: listIsLoading ? "hidden" : "visible",
                    marginTop: isSmallScreen ? "10px" : null,
                    marginBottom: "-35px",
                    zIndex: "9999",
                    marginLeft: "auto",
                }}
                disabled={exporting}
                onClick={handleExportPDF}>
                {exporting ? 'Exporting...' : 'Export'}
            </Button>);
    };

    function OpenUserMainOffice({ user, isSmallScreen = false }) {
        const uniqueName = user?.has_personal_main_office_conversation ? user?.main_office_conversation_unique_name : null;

        // Parse pieces even if null so hooks stay top-level (no conditional calls)
        const parts = uniqueName ? String(uniqueName).split("::") : [];
        const teamSlug = parts.length > 1 ? parts[1] : (parts[0] ?? "");
        const linkStr =
            uniqueName && teamSlug
                ? `/chat/${teamSlug}/conversations/${uniqueName}/interactivechat`
                : "#";

        const handleClick = React.useCallback(
            async (e) => {
                if (!uniqueName) return;
                e.preventDefault();
                // Open the chat regardless of the repair outcome: ChatScreen retries the
                // repair itself and renders a proper no-access/error screen if it fails.
                await ensure_conversation_is_active_synced(uniqueName).catch(err => {
                    console.warn(`UserProfile: ensure_conversation_is_active_synced failed for ${uniqueName}:`, err);
                });
                window.open(linkStr, "_blank", "noopener,noreferrer");
            },
            [uniqueName, linkStr]
        );

        if (!uniqueName) return null;

        return (
            <a
                href={linkStr}
                target="_blank"
                rel="noreferrer"
                onClick={handleClick}
                style={{
                    marginTop: isSmallScreen ? 10 : undefined,
                    marginBottom: -35,
                    zIndex: 9999,
                }}
            >
                <Button title="Chat with this user in Main Office">
                    <ChatBubbleOutlineIcon style={{height: '1.25rem'}} />
                    &nbsp;Main Office
                </Button>
            </a>
        );
    }

    function CustomToolbar() {
        return (
            <GridToolbarContainer >
                <OpenUserMainOffice
                    user={user}
                />
                <ExportPrintButton/>

            </GridToolbarContainer>
        );
    }

    const generateNavigateBackButtonURL = () => {
        if (parseInt(new URLSearchParams(props.lastURLParams).get('tab')) === 4 && props.data.is_tour_operator_staff) {
            return 'tab=1'
        }
        if (parseInt(new URLSearchParams(props.lastURLParams).get('tab')) === 4 && !props.data.is_tour_operator_staff) {
            return 'tab=0'
        }
        return props.lastURLParams
    }

    const EmailVerifiedIcon = props.data.is_email_verified ? <VerifiedUserIcon color="success" fontSize='small'/> : null;
    const PhoneVerifiedIcon = props.data.phone_verified ? <VerifiedUserIcon color="success" fontSize='small'/> : null;

    return (
        <div className={classes.root}>
            <FixedLinearProgress visibility={syncActive} />
            <PageMeta title={`${user.display_name}`} />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    formInput={confirmationDialogAction === 'resend_email' ? emailResendRef.current : ['resend_sms', 'resend_whatsapp'].includes(confirmationDialogAction) ? phoneResendRef.current : null}
                    formInputRef={confirmationDialogAction === 'resend_email' ? emailResendRef : ['resend_sms', 'resend_whatsapp'].includes(confirmationDialogAction) ? phoneResendRef : null}
                    validate={confirmationDialogAction === 'resend_email' ? validateEmailMsg : ['resend_sms', 'resend_whatsapp'].includes(confirmationDialogAction) ? validatePhoneMsg : null}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <Grid container alignItems="center" justifyContent="space-between">
                <NavigateBackBar label={user.display_name} url={'/user?' + generateNavigateBackButtonURL()} labelDescription={'User'} />
                {currentTab === 4 && <Grid item xs={12} lg={4} pr={isSmallScreen ? 0 : 2} pl={isSmallScreen ? 2 : 0} pb={1} container justifyContent={isSmallScreen ? "flex-start" : "flex-end"} alignContent={"center"} gap={"0.25rem"}>
                    Location:   <LocationStatusWidget className={classes.widgetValue} status={user.location_status} />
                </Grid>}
            </Grid>

            <Divider />
            <Tabs value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto">
                <Tab label="General" {...a11yProps(0)} />
                
                {(true || user.is_guest) && <Tab label="History Log" {...a11yProps(1)} />}
                {hasPerm('agroup', 'read') && <Tab label="Permissions" {...a11yProps(2)} />}
                {
                    //<Tab label="Email Logs" {...a11yProps(1)} />
                }
                {hasPerm('user', 'view_history') && <Tab label="Change history" {...a11yProps(3)} />}
                <Tab label="Location History" {...a11yProps(4)} />
                <Tab label="Trips" {...a11yProps(5)} />
            </Tabs>
            <TabPanel {...tabPanelProps(0)}>
                <Box p={2} className={classes.userProfile}>
                    <Grid container direction="row">
                        <Grid item xs={12}>
                            <Typography variant="h4" style={{ marginLeft: !isSmallScreen ? '0.75rem' : 0, fontWeight: '500', marginBottom: '2rem' }}>
                                User Profile
                            </Typography>
                        </Grid>
                        <Grid item xs={12} container justifyContent="flex-start" flexWrap={isSmallScreen ? "wrap" : "nowrap"} >
                            <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4}  >
                                <Typography style={{ fontWeight: '400', textTransform: 'uppercase', marginLeft: '0.75rem', color: '#202020' }}>Basic info</Typography>
                                <DisplayField classes={classes} label="Full Name" img={user.avatar} showAvatar={true} value={`${user.first_name || ''} ${user.last_name || ''}`.trim()} nowrap />
                                <DisplayField classes={classes} label={<>Email {EmailVerifiedIcon}</>} value={user.email} nowrap link={`mailto:${user.email}`} />
                                <div style={{ display: "flex" }}><DisplayField classes={classes} label={<>Phone {PhoneVerifiedIcon}</>} value={user.phone} link={`tel:${user.phone}`} /> {user.phone && user.sms_is_undeliverable && <div title={"Number is unreachable"} style={{ display: "flex", alignItems: "center" }}><ErrorOutlineIcon style={{ marginTop: "18px", color: "#999999", height: "18px", width: "18px" }} /></div>}</div>
                                <DisplayField classes={classes} label="Language" value={user.lang} />
                                <DisplayField classes={classes} label="OTA" value={user.ota?.name ?? '-'} />
                                <DisplayField classes={classes} label="Bio" value={user.bio} />
                            </Grid>
                            <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography style={{ fontWeight: '400', textTransform: 'uppercase', marginLeft: '0.75rem', color: '#202020' }}>Other info</Typography>
                                <DisplayField classes={classes} label="Platform" value={getUserPlatform(user) || ''} />
                                <DisplayField classes={classes} label="Notifications Status" component={<NotificationStatusWidget className={classes.widgetValue} status={user.notifications_status} />} />
                                <DisplayField classes={classes} label="Location Status" component={<LocationStatusWidget className={classes.widgetValue} status={user.location_status} />} />
                                <DisplayField classes={classes} label="Date joined" value={moment.utc(user.date_joined).format('MMM DD, YYYY HH:mm:ss Z')} />
                                <DisplayField classes={classes} label="Last login" value={moment.utc(user.last_login).format('MMM DD, YYYY HH:mm:ss Z')} />
                                <div className={classes.field}>
                                    <Typography variant="body2" className={classes.label}>Consents</Typography>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {user.allow_ads ? <Tooltip title="Marketing consent granted"><LocalGroceryStoreIcon color="success"/></Tooltip> : <Tooltip title="Marketing consent not granted"><LocalGroceryStoreOutlinedIcon color="error"/></Tooltip>}
                                        {user.privacy_policy_consent ? <Tooltip title="Privacy policy consent granted"><PolicyIcon color="success"/></Tooltip> : <Tooltip title="Privacy policy consent not granted"><PolicyOutlinedIcon color="error"/></Tooltip>}
                                        {user.allow_email_nf ? <Tooltip title="Email notifications allowed"><EmailIcon color="success"/></Tooltip> : <Tooltip title="Email notifications not allowed"><EmailOutlinedIcon color="error"/></Tooltip>}
                                        {user.allow_sms_nf ? <Tooltip title="SMS notifications allowed"><SmsIcon color="success"/></Tooltip> : <Tooltip title="SMS notifications not allowed"><SmsOutlinedIcon color="error"/></Tooltip>}
                                        {user.allow_whatsapp_nf ? <Tooltip title="WhatsApp notifications allowed"><WhatsAppIcon color="success"/></Tooltip> : <Tooltip title="WhatsApp notifications not allowed"><WhatsAppIcon color="error"/></Tooltip>}
                                        {user.allow_push_nf ? <Tooltip title="Push notifications allowed"><NotificationsIcon color="success"/></Tooltip> : <Tooltip title="Push notifications not allowed"><NotificationsNoneOutlinedIcon color="error"/></Tooltip>}
                                    </Stack>
                                </div>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} mb={4}>
                            <Typography style={{ fontWeight: '400', textTransform: 'uppercase', marginLeft: '0.75rem', color: '#202020' }}>Contacts</Typography>
                            <UserContactList
                                user={user}
                                contacts={user.contacts}
                                readOnly={true}
                            />
                        </Grid>
                        {isCuratedPlanetUser && <>
                            <Grid item xs={12}>
                                <Typography style={{ fontWeight: '400', textTransform: 'uppercase', marginLeft: '0.75rem', color: '#202020' }}>Internal properties</Typography>
                            </Grid>
                            { typeof user.passport_number !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Passport: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.passport_number}</Typography>
                            </Grid> }
                            { typeof user.date_of_birth !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Date of birth: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.date_of_birth}</Typography>
                            </Grid> }
                            { typeof user.address !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Address: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.address}</Typography>
                            </Grid> }
                            { typeof user.branchio_link !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Deep link: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.branchio_link}</Typography>
                            </Grid> }
                            { typeof user.twilio_identity !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Twilio identity: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.twilio_identity}</Typography>
                            </Grid> }
                            { typeof user.main_office_conversation_unique_name !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Main office conversation: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.main_office_conversation_unique_name}</Typography>
                            </Grid> }
                            { user.trip_count && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Trip count: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.trip_count}</Typography>
                            </Grid> }
                            { user.ota_email && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">OTA email: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.ota_email}</Typography>
                            </Grid> }
                            { user.email_is_fake && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Fake email: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.email_is_fake ? 'Yes' : 'No'}</Typography>
                            </Grid> }
                            { user.airbnb_user_id && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Airbnb user ID: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.airbnb_user_id}</Typography>
                            </Grid> }
                            { user.position_status && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Position status: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.position_status}</Typography>
                            </Grid> }
                            { typeof user.bypass_auth !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Bypass magic auth: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.bypass_auth ? 'Yes' : 'No'}</Typography>
                            </Grid> }
                            { typeof user.exclude_from_chat !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Exclude from chat: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.exclude_from_chat ? 'Yes' : 'No'}</Typography>
                            </Grid> }
                            { typeof user.exclude_from_group_chat !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Exclude from group chat: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.exclude_from_group_chat ? 'Yes' : 'No'}</Typography>
                            </Grid> }
                            { typeof user.has_admin_access !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Has admin access: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.has_admin_access ? 'Yes' : 'No'}</Typography>
                            </Grid> }
                            { typeof user.is_admin !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Is admin: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.is_admin ? 'Yes' : 'No'}</Typography>
                            </Grid> }
                            { typeof user.is_guest !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Is guest: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.is_guest ? 'Yes' : 'No'}</Typography>
                            </Grid> }
                            { typeof user.is_rbac_user !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Is RBAC user: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.is_rbac_user ? 'Yes' : 'No'}</Typography>
                            </Grid> }
                            { typeof user.is_tour_operator_staff !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Is tour operator staff: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.is_tour_operator_staff ? 'Yes' : 'No'}</Typography>
                            </Grid> }
                            { typeof user.sms_is_undeliverable !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">SMS is undeliverable: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.sms_is_undeliverable ? 'Yes' : 'No'}</Typography>
                            </Grid> }
                            { typeof user.sms_allowed_via_twilio !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">SMS allowed via Twilio: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.sms_allowed_via_twilio ? 'Yes' : 'No'}</Typography>
                            </Grid> }
                            { typeof user.whatsapp_allowed_via_twilio !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">WhatsApp allowed via Twilio: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.whatsapp_allowed_via_twilio ? 'Yes' : 'No'}</Typography>
                            </Grid> }
                            { typeof user.whatsapp_status !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">WhatsApp status: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{WHATSAPP_STATUS[user.whatsapp_status]}</Typography>
                            </Grid> }
                            { typeof user.roles !== 'undefined' && <Grid item xs={12} md={4} mr={isSmallScreen ? 0 : 4} mt={isSmallScreen ? 4 : 0}>
                                <Typography variant="body2" className={classes.label} component="span">Roles: </Typography>
                                <Typography variant="body2" className={classes.value} component="span">{user.roles.map((r) => roleMap[r]?.name).join(', ')}</Typography>
                            </Grid> }

                        </>}
                    </Grid>
                </Box>

                <Box p={1} className={classes.buttonPanel}>
                    {/* CP-8427: bottom right, primary action on the right, as on the preview panels: [DELETE] [EDIT]. */}
                    <FormActionButtons
                        onDelete={handleDelete}
                        deletePerm="user.delete"
                        deleteRef={deleteRef}
                        onSubmit={handleEdit}
                        submitRef={editRef}
                        submitLabel="Edit"
                        submitIcon={<EditIcon />}
                    />
                </Box>
            </TabPanel>
            {(true || user.is_guest) && <TabPanel {...tabPanelProps(1)}>
                {/* CP-8250: the panel used to inset only on the left, so the filters, the
                    Export button and the message rows all ran flush to the right edge and
                    disagreed with each other. Pad both sides so everything in the tab
                    shares one content box. */}
                <Box style={{ paddingLeft: '1rem', paddingRight: '1rem', maxWidth: isExporting ? "9.5in" : '85rem' }}>
                    <Grid item style={{ height: isExporting ? 'auto' : '81vh', width: '100%', fontSize: '80%', paddingBottom: '6rem' }}>
                        <UserChatHistoryToolbar
                            resource={chatListResource}
                            // id={activeUser ? activeUser.id : null}
                            id={null}
                            chatListQuery={chatListQuery}
                            onQueryChange={onChatListQueryChange}
                            chatAPIFilters={chatAPIFilters}
                            setChatAPIFilters={setChatAPIFilters}
                            chatSelectedTourFilter={chatSelectedTourFilter}
                            setChatSelectedTourFilter={setChatSelectedTourFilter}
                            chatListSortOrder={chatListSortOrder}
                            setChatListSortOrder={setChatListSortOrder}
                            isSmallScreen={isSmallScreen}
                            user={user}
                        />
                        <div style={{ width: '0%', marginBottom: '-1.0rem' }}></div>
                        <RecordList
                            className={classes.recordList}
                            resource={chatListResource + '/' + user.id.toString()}
                            apiFilters={chatAPIFilters}
                            hiddenAPIFilters={chatAPIFilters}
                            checkboxSelection={false}
                            disableSelectionOnClick={true}
                            sortModel={[{ field: 'timestamp', sort: chatListSortOrder }]}
                            query={chatListQuery}
                            isSmallScreen={isSmallScreen}
                            disableListBorder={false}
                            limitHeightForMobile={'40vh'}
                            page={props.staffLastDataGridPage}
                            setLastDataGridPage={props.setStaffLastDataGridPage}
                            perPage={10}
                            setLastDataGridPerPage={props.setStaffLastDataGridPerPage}
                            getRowHeight={() => 'auto'}
                            borderDisplay="none"
                            headerDisplay="none"
                            ignoreHeaderOrdering={true}
                            //headerHeight={0}
                            toolbar={CustomToolbar}
                            isExporting={isExporting}
                            virtualScrollerMarginTop={10}
                            setIsLoading={setListIsLoading}
                            columns={[
                                {
                                    field: 'associated_twilio_conversation_unique_name',
                                    headerName: 'Go to chat',
                                    sortable: false,
                                    flex: 0.25,
                                    align: 'center',
                                    valueGetter: (p) => p.row.associated_twilio_conversation_unique_name,
                                    renderCell: (p) => {
                                        const unique_name = p.row.associated_twilio_conversation_unique_name;
                                        const gtcc_id = unique_name ? unique_name.split('::')[1] : '';
                                        const conversation_is_active = p.row.associated_twilio_conversation_non_guest_participants_state?.includes('active') || false;

                                        return (
                                            <Box height="100%" style={{ marginTop: '8px', display: 'flex', flexDirection: 'column' }}>
                                                <div style={{ flex: 1 }}>
                                                    {conversation_is_active ? (
                                                        <a href={'/chat/' + gtcc_id + '/conversations/' + unique_name + '/interactivechat'} target="_blank" rel="noreferrer">
                                                            <span style={{ display: 'flex', alignItems: 'center', height: 'fit-content', border: '1px solid #D8D8D8', borderRadius: '8px', padding: '4px' }}>
                                                                <ActionOpenIcon style={{ fontSize: '18px' }} color="primary" />
                                                            </span>
                                                        </a>
                                                    ) : (
                                                        <span style={{ display: 'flex', alignItems: 'center', height: 'fit-content', border: '1px solid #D8D8D8', borderRadius: '8px', padding: '4px' }}>
                                                            <ChatIcon style={{ fontSize: '18px' }} />
                                                        </span>
                                                    )}
                                                </div>
                                                <div class="border" style={{ width: '50%', flex: 10, borderRight: '1px solid #D8D8D8' }}></div>
                                            </Box>
                                        );
                                    },
                                },
                                {
                                    field: 'sender_name',
                                    headerName: 'Sender',
                                    sortable: true,
                                    flex: 11,
                                    valueGetter: (p) => p.row.sender?.display_name,
                                    renderCell: (p) => {
                                        const d1 = new Date(p.row.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                        const d2 = new Date(p.row.timestamp).toLocaleString('en-US', { hour: 'numeric', minute: 'numeric' });

                                        const role_map = { admin: 'Admin', guide: 'Tour Guide', guest: 'Guest' };
                                        let role_text = role_map[p.row.sender_conversation_role];

                                        // Email bodies are intentional HTML (see the is_html_email handling below).
                                        // Every other source is plain text, where a raw "<" would be parsed as a tag
                                        // by linkify-html and the rest of the message dropped by DOMPurify.
                                        const rawChatText = p.row.text_plus_media_url || '';
                                        let chatText = (p.row.source === 'email' ? rawChatText : escape_html(rawChatText))
                                            .split('\n').join('<br>').split('<p>\r\n').join('<p>').split('\r\n</p>').join('</p>');

                                        if(p.row.source === 'whatsapp'){
                                            // if WhatsApp, convert the WhatsApp markdown to html for intuitive viewing
                                            // (skips URLs so links with ~/*/_ aren't mangled — see chat_utils)
                                            chatText = whatsapp_markup_to_html(chatText);
                                        }

                                        // CP-8116: collapse raw attachment/media URLs into short labeled links.
                                        // Email bodies are intentional HTML — never rewrite those (see chat_utils).
                                        if (p.row.source !== 'email') {
                                            chatText = shorten_attachment_links(chatText);
                                        }

                                        let sanitizedText = DOMPurify.sanitize(linkify(chatText), { ADD_ATTR: ['target'] });

                                        // Show the formatted email if expanded
                                        if (p.row.source === 'email' && (p.row.id === chatTextExpandedRowID || isExporting)) {
                                            let regex = /<[^>]*>/g;

                                            // remove newlines from within tags (e.g. <div \n id="abc">)
                                            sanitizedText = p.row.text_plus_media_url?.replace(regex, function (match) {
                                                return match.replace(/[\n\r]/g, '');
                                            });

                                            const linebreaksAfterSubjectLine = '\n\n';
                                            // This is a hack to find the subject line (e.g. <b><em>SUBJECT: TEST</em></b>\n\n)
                                            // IMPORTANT: If I update the line breaks in the back end to <br><br>, change this!!!
                                            const i1 = sanitizedText.indexOf(linebreaksAfterSubjectLine);
                                            //const i2 = sanitizedText.indexOf('<br><br>')
                                            let is_html_email = false;
                                            if (i1 > 1) {
                                                let bodyText = sanitizedText.substring(i1 + linebreaksAfterSubjectLine.length);
                                                // If the body appears to be html only (i.e. all encapsulated within < and >), then remove the \n and/or \r newlines
                                                if (bodyText.trim().length >= 2 && bodyText.trim()[0] === '<' && bodyText.trim()[bodyText.trim().length - 1] === '>') {
                                                    // (this was formatting oddly, so just ensure proper use of <pre> tags)
                                                    //sanitizedText = sanitizedText?.replace(regex2, function (match) {
                                                    //   return match.replace(/[\n\r]/g, '');
                                                    //});
                                                    is_html_email = true;
                                                }
                                            }

                                            if (!is_html_email) {
                                                // replace newline characters with <br> tags
                                                sanitizedText = sanitizedText?.split('<p>\r\n').join('<p>').split('\r\n</p>').join('</p>').split('\n').join('<br>');
                                            } else {
                                                // Strip zero font-size/line-height from inline styles so content
                                                // after the email's table structure remains visible. These are
                                                // MSO-specific whitespace-collapse hacks that cause the browser to
                                                // inherit invisible text when the full HTML doc is rendered inline.
                                                sanitizedText = sanitizedText
                                                    .replace(/font-size\s*:\s*0\s*(px|pt)?\s*(?=[;"])/gi, '')
                                                    .replace(/line-height\s*:\s*0\s*(px|pt)?\s*(?=[;"])/gi, '');
                                            }
                                        }

                                        // Make sure that <pre> tags wrap
                                        if (p.row.source === 'email') sanitizedText = sanitizedText?.replace('<pre>', '<pre style="white-space: pre-wrap;">');

                                        let expandable = false;

                                        if (sanitizedText.includes('<br>') || sanitizedText.length >= 180) {
                                            expandable = true;
                                        }

                                        if (p.row.sender_conversation_role === 'guest' && p.row.sender && p.row.sender.is_guide) {
                                            role_text = 'Tour Guide';
                                        } else if (p.row.sender_conversation_role === 'guest' && p.row.sender && p.row.sender.is_admin) {
                                            role_text = 'Admin';
                                        }
                                        const role_bg_color_map = { Admin: '#B7EBE3', 'Tour Guide': '#E4E4F9', Guest: '#F8D7DA' };

                                        let sent_via = p.row.source;
                                        if (sent_via === 'chat') {
                                            sent_via = 'In-App';
                                        } else if (sent_via === 'email') {
                                            sent_via = 'Email';
                                        } else if (sent_via === 'sms') {
                                            sent_via = 'SMS';
                                        } else if (sent_via === 'whatsapp') {
                                            sent_via = 'WhatsApp';
                                        } else if (sent_via === 'airbnb') {
                                            sent_via = 'Airbnb';
                                        }
                                        /*if (p.row.chat_source_platform.toUpperCase().includes('CLIENT APP') || p.row.chat_source_platform.toUpperCase().includes('AUTOMATED SYSTEM')) {
                                            sent_via = 'In-App';
                                        } else if (p.row.chat_source_platform.toUpperCase().includes('EMAIL')) {
                                            sent_via = 'Email';
                                        } else if (p.row.chat_source_platform.toUpperCase().includes('SMS')) {
                                            sent_via = 'SMS';
                                        }*/

                                        let conversation_display_name = '';
                                        const channelTopicID = p.row.associated_twilio_conversation_unique_name?.split('::')[0];
                                        const channelSubjectID = p.row.associated_twilio_conversation_unique_name?.split('::')[2];
                                        if (p.row.recipient) {
                                            conversation_display_name = p.row.recipient.display_name;
                                        } else if (channelSubjectID === 'announcements') {
                                            conversation_display_name = 'Announcements';
                                        } else if (channelSubjectID === 'guides_and_staff') {
                                            conversation_display_name = 'INTERNAL: Guides/Staff';
                                        } else if (channelSubjectID === 'group_chat') {
                                            conversation_display_name = 'Group Chat';
                                        } else if (channelTopicID === 'main_office') {
                                            conversation_display_name = 'Main Office';
                                        } else if (channelTopicID === 'app_support') {
                                            conversation_display_name = 'App Support';
                                        } else if (user.id === parseInt(channelSubjectID)) {
                                            conversation_display_name = 'Tour Guides/Support';
                                        } else {
                                            conversation_display_name = 'Chat';
                                        }

                                        return (
                                            <div ref={p.row.id === chatTextExpandedRowID ? componentToPrintRef : null} style={{ fontSize: '16px', marginTop: '8px', marginBottom: '8px', width: '100%' }}>
                                                <style type="text/css" media="print">
                                                    {`
                                                        @page { size: landscape; margin: 20mm; }
                                                        .MuiDataGrid-virtualScroller { height: auto !important; overflow: visible !important; }
                                                        .MuiDataGrid-virtualScrollerContent { height: auto !important; overflow: visible !important; }
                                                        .MuiDataGrid-virtualScrollerRenderZone { position: relative !important; transform: none !important; }
                                                        .MuiDataGrid-main { height: auto !important; overflow: visible !important; }
                                                        .MuiDataGrid-root { height: auto !important; overflow: visible !important; }
                                                        .MuiDataGrid-row { page-break-inside: avoid; break-inside: avoid; }
                                                    `}
                                                </style>
                                                {/* open the print dialog if print-to-pdf icon was clicked and this is the row to print*/}
                                                {componentToPrintRef.current != null && printWasClickedRef.current === true ? printRow() : null}
                                                <div>
                                                    <span style={{ fontWeight: 500 }}>{p.row.sender?.display_name || p.row.automated_message_sender_name || p.row.sender_name}</span>
                                                    <span> </span>
                                                    {p.row.sender_conversation_role ? <span style={{ fontSize: '14px', backgroundColor: role_bg_color_map[role_text], padding: '2px 8px', borderRadius: '2px' }}>{role_text}</span> : ''}
                                                    <span> to </span>
                                                    <span style={{ fontWeight: 500 }}>{conversation_display_name}</span>
                                                    <span> sent </span>
                                                    <span style={{ fontWeight: 500 }}>{sent_via}</span>
                                                    {p.row.source === 'email' && p.row.recipient?.id === user.id ? (
                                                        <IconButton
                                                            title={'Resend email'}
                                                            style={{ marginLeft: '.25rem', height: '1em', width: '1em', marginBottom: '.15em' }}
                                                            onClick={(ev) => {
                                                                resendEmailRef.current = p.row.source_id;
                                                                emailResendRef.current = p.row.recipient_email_address || user.email;
                                                                handleResendEmail();
                                                            }}
                                                        >
                                                            <ResendEmailIcon style={{ transform: 'scale(2)' }} />
                                                        </IconButton>
                                                    ) : p.row.source === 'sms' && p.row.recipient?.id === user.id ? (
                                                        <IconButton
                                                            title={'Resend SMS'}
                                                            style={{ marginLeft: '.25rem', height: '1em', width: '1em', marginBottom: '.15em' }}
                                                            onClick={(ev) => {
                                                                resendEmailRef.current = p.row.source_id;
                                                                phoneResendRef.current = p.row.recipient_phone_number || user.phone;
                                                                handleResendSms();
                                                            }}
                                                        >
                                                            <ResendEmailIcon style={{ transform: 'scale(2)' }} />
                                                        </IconButton>
                                                    ) : p.row.source === 'whatsapp' && p.row.recipient?.id === user.id ? (
                                                        <IconButton
                                                            title={'Resend WhatsApp message'}
                                                            style={{ marginLeft: '.25rem', height: '1em', width: '1em', marginBottom: '.15em' }}
                                                            onClick={(ev) => {
                                                                resendEmailRef.current = p.row.source_id;
                                                                phoneResendRef.current = (p.row.recipient_phone_number || user.phone)?.replace('whatsapp:', '');
                                                                handleResendWhatsApp();
                                                            }}
                                                        >
                                                            <ResendEmailIcon style={{ transform: 'scale(2)' }} />
                                                        </IconButton>
                                                    ) : (
                                                        <span style={{ marginLeft: '.35rem' }}></span>
                                                    )}

                                                    <IconButton
                                                        title={'Print to PDF'}
                                                        style={{ height: '1em', width: '1em' }}
                                                        onClick={(e) => {
                                                            expandChatTextAndPrint(e, p.row.id);
                                                        }}
                                                    >
                                                        <PictureAsPdfOutlinedIcon style={{ color: '#007AFF', height: '.8em', marginBottom: '.15em' }} />
                                                    </IconButton>
                                                    {p.row.associated_twilio_conversation_channel_name_date_inside || p.row.event_code_summary_label ? (
                                                        <span style={{ fontSize: '14px', float: 'right', padding: '2px 8px' }}>
                                                            <span style={{ color: '#F2994A', fontWeight: 500 }}>{p.row.event_code_summary_label}</span>
                                                            {p.row.associated_twilio_conversation_channel_name_date_inside ? (
                                                                <span style={{ backgroundColor: '#EAF4FF', borderRadius: '2px', marginLeft: '7px' }}>
                                                                    {p.row.associated_twilio_conversation_channel_name_date_inside}
                                                                    <br />
                                                                </span>
                                                            ) : (
                                                                ''
                                                            )}
                                                            {p.row.subject_gtus_booking_refs && <span title={"BR: " + p.row.subject_gtus_booking_refs.join('\nBR: ')} style={{ float: 'right', marginTop: p.row.associated_twilio_conversation_channel_name_date_inside ? '2px' : null, fontSize: '0.95rem' }}>BR:&nbsp;{p.row.subject_gtus_booking_refs[0]}{p.row.subject_gtus_booking_refs?.length > 1 && <span style={{fontSize: '0.6rem', verticalAlign: "super"}}>&nbsp;(+{p.row.subject_gtus_booking_refs?.length - 1})</span>}</span>}
                                                        </span>
                                                    ) : (
                                                        ''
                                                    )}
                                                </div>
                                                <EmailDeliveryFailure failure={p.row.delivery_failure} />
                                                <div>
                                                    {!p.row.delivery_failure && <span style={{ marginRight: '4px', marginLeft: '3px' }}>
                                                        {p.row.was_read ? (
                                                            <span title="Read">
                                                                <DoneAllIcon style={{ color: '#007AFF', fontSize: '16px', verticalAlign: 'middle', marginBottom: '4px' }} />
                                                            </span>
                                                        ) : (
                                                            <span title={p.row.source === 'sms' ? 'Delivered' : 'Unread'}>
                                                                <DoneAllIcon style={{ fontSize: '16px', verticalAlign: 'middle', marginBottom: '4px' }} />
                                                            </span>
                                                        )}
                                                    </span>}
                                                    <span style={{ color: '#747475', fontSize: '14px', marginLeft: p.row.delivery_failure ? '3px' : undefined }}>{d1 + ' at ' + d2}</span>
                                                    {p.row.recipient_email_address && (
                                                        <span style={{ color: '#747475', fontSize: '14px', marginLeft: '1px'}}> to {p.row.recipient_email_address}</span>
                                                    )}
                                                    {p.row.recipient_phone_number && (
                                                        <span style={{ color: '#747475', fontSize: '14px', marginLeft: '1px'}}> to {p.row.recipient_phone_number}</span>
                                                    )}
                                                </div>
                                                <div style={{ cursor: 'pointer' }} onClick={(e) => expandChatText(e, p.row.id)}>
                                                    <div style={styles.chatTextDiv(p.row.id === chatTextExpandedRowID || isExporting)} >
                                                        <span style={{ position: 'absolute', top: 0, right: 5, fontSize: '20px' }}>{expandable ? (p.row.id === chatTextExpandedRowID || isExporting) ? (isExporting ? '' : <KeyboardArrowUpIcon />) : <KeyboardArrowDownIcon /> : ''}</span>
                                                        <span dangerouslySetInnerHTML={{ __html: sanitizedText.replace('<a', '<a target="_blank"') }}>
                                                        </span>
                                                    </div>
                                                </div>
                                                {Array.isArray(p.row.media_urls) && p.row.media_urls.length > 0 && (
                                                    <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                        {p.row.media_urls.map((url, idx) => {
                                                            const isImage = isImageMediaUrl(url);
                                                            if (isImage) {
                                                                return (
                                                                    <a key={idx} href={url} target="_blank" rel="noreferrer">
                                                                        <img
                                                                            src={url}
                                                                            alt=""
                                                                            style={{ maxWidth: '120px', maxHeight: '120px', borderRadius: '6px', border: '1px solid #ddd', objectFit: 'cover', display: 'block' }}
                                                                        />
                                                                    </a>
                                                                );
                                                            }
                                                            const filename = friendlyAttachmentFilename(url);
                                                            return (
                                                                <a
                                                                    key={idx}
                                                                    href={url}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    download
                                                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', backgroundColor: '#EEE', borderRadius: '6px', fontSize: '12px', textDecoration: 'none', color: '#333' }}
                                                                >
                                                                    <AttachFileIcon style={{ fontSize: '16px' }} />
                                                                    {filename}
                                                                </a>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                {/*p.row.id === chatTextExpandedRowID && <button onClick={handlePrint}>Print this out!</button>*/}

                                                {/*<PDFViewer>
                                                    <PDFDoc
                                                    
                                                        channel_line={
                                                            p.row.associated_twilio_conversation_channel_name
                                                        }
                                                        sender_receiver_line={
                                                                <div>
                                                                    <span style={{ fontWeight: 500 }}>{p.row.sender?.display_name || p.row.automated_message_sender_name || p.row.sender_name}</span>
                                                                    <span> to </span>
                                                                    <span style={{ fontWeight: 500 }}>{conversation_display_name}</span>
                                                                    <span> sent </span>
                                                                    <span style={{ fontWeight: 500 }}>{sent_via}</span>

                                                                </div>
                                                        }
                                                        status_line={
                                                            <div>
                                                                {p.row.was_read ? ( "Read" ) : ("Delivered")}
                                                            </div>
                                                        }
                                                        datetime_line={
                                                            <div>
                                                                <span>{d1 + ' at ' + d2}</span>
                                                            </div>
                                                        }
                                                        text_line={
                                                            <div>
                                                                {chatText}
                                                            </div>
                                                        }
                                                    />
                                                </PDFViewer>*/}
                                            </div>
                                        );
                                    },
                                },
                            ]}
                        />
                    </Grid>
                </Box>
            </TabPanel>}
            {hasPerm('agroup', 'read') && <TabPanel {...tabPanelProps(2)}>
                <UserProfilePermissions id={user.id} />
            </TabPanel>}
            <TabPanel {...tabPanelProps(3)}>
                <RevisionHistoryDialog open={currentTab === 3} resource={props.resource} id={user.id} />
            </TabPanel>
            <TabPanel {...tabPanelProps(4)}>
                <Grid item xs={12}>
                    <LocationHistoryMap user={user} currentTab={currentTab} />
                </Grid>
            </TabPanel>
            <TabPanel {...tabPanelProps(5)}>
                <UserProfileTrips user={user} />
            </TabPanel>
        </div>
    );
};

const styles = {
    chatTextDiv: (isSelected) => ({
        fontSize: '12px',
        paddingTop: '8px',
        paddingBottom: '8px',
        paddingLeft: '12px',
        paddingRight: '36px',
        backgroundColor: '#F5F5F5',
        borderRadius: '8px',
        maxHeight: isSelected ? 'none' : '1.75rem',
        overflow: isSelected ? 'visible' : 'hidden',
        whiteSpace: isSelected ? null : 'nowrap',
        textOverflow: isSelected ? null : 'ellipsis',
        cursor: 'pointer',
        width: 'calc(100% - 54px)',
        position: "relative"
    }),
};

const UserDetailsProvider = (props) => {
    const routeParams = useParams();
    const resource = 'user';
    const id = routeParams.id;
    const { hasPerm } = React.useContext(ContextStore);

    if (!hasPerm(resource, 'read')) {
        return <AccessDeniedView />
    }
    return (
        <RecordDetails resource={resource} id={id}>
            <RecordDetailsContext.Consumer>{(data) => <UserProfile lastURLParams={props.lastURLParams} resource={resource} id={id} data={data} />}</RecordDetailsContext.Consumer>
        </RecordDetails>
    );
};

export default UserDetailsProvider;
